<script lang="ts" setup>
import { Head, usePage } from "@inertiajs/vue3";
import HeadingComponent from "../../Components/HeadingComponent.vue";
import EventPublicWrapper from "../EventPublic/EventPublicWrapper.vue";
import LocalEventTime from "@/Components/LocalEventTime.vue";
import { entityDecoder, getDeviceNavigationAppUrl, linkify } from "@/Helpers/helperFunctions.js";
import { onMounted, onUnmounted, reactive, ref } from "vue";
import { Disclosure, DisclosureButton, DisclosurePanel } from "@headlessui/vue";
import { DateTime } from "luxon";
import { PencilIcon } from "@heroicons/vue/20/solid";
import { deepMerge } from "@/Helpers/helperFunctions";

import "/node_modules/flag-icons/css/flag-icons.min.css";

import { Event } from "@/types";

interface Props {
    event: Event;
    canRegisterForEvent: boolean;
    is_event_full: boolean;
}

import { ArrowTopRightOnSquareIcon } from "@heroicons/vue/24/solid/index.js";

import { startLocalEventTimeTicker, stopLocalEventTimeTicker } from "@/Helpers/localEventTimeTicker.js";
import EventInformationMap from "@/Pages/EventPublic/Components/EventInformationMap.vue";
import FwbTooltip from "@/Components/FwbTooltip/FwbTooltip.vue";
import Modal from "@/Components/Modal.vue";
import Button from "@/Components/Button.vue";
import TooltipIcon from "@/Components/TooltipIcon.vue";
import eventBus from "@/Helpers/eventBus";
import SendMessageModal from "@/Components/SendMessageModal.vue";

const props = withDefaults(defineProps<Props>(), {});

const calendarToggle = ref(false);

const defaultPenalties = usePage().props.app.penalties;
defaultPenalties.waypoints = usePage().props.app.waypoints;
const myPenalties = deepMerge({}, defaultPenalties, usePage().props.event.penalties);

// const myTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
// const myCountryCode = usePage().props.timezones[myTimezone]?.country_code;
// console.log("myTimezone", myTimezone);
// console.log("myCountryCode", myCountryCode);

const disclosure = ref([]);

const closeOthers = (idx) => {
    disclosure.value.filter((d, i) => i !== idx).forEach((c) => c());
};

function getEventTermsCondsHtmlLink(event) {
    if (!event.hashid) {
        return "Save Event to see the link to terms and conditions.";
    }
    const termsLink = route("event-terms-and-conditions", { event_hashid: event.hashid });
    const termsLinkContent = `Event <i>"${
        usePage().props.meta_event_name
    }"</i> Terms And Conditions <svg class="inline h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"></path></svg>`;

    return `<a href="${termsLink}" target="_blank">${termsLinkContent}</a>`;
}

const infos = ref([]);
onMounted(() => {
    // console.log("onMounted EventInformation");

    // do not run local event time ticker in SSR
    if (typeof window !== "undefined") {
        startLocalEventTimeTicker(props.event.timezone);
    }

    infos.value = props.event.informations;

    if (props.event.description) {
        infos.value.unshift({
            title: "Rules and Regulations",
            description: props.event.description,
        });
    }

    infos.value.push({
        title: "Terms and Conditions",
        terms: getEventTermsCondsHtmlLink(props.event),
    });
});

onUnmounted(() => {
    // console.log("onUnmounted EventInformation");
    // do not run local event time ticker in SSR
    if (typeof window !== "undefined") {
        stopLocalEventTimeTicker();
    }
});

const myImageModalProps = reactive({
    modalImageSrc: "",
    showModal: false,
});

function showImageModal(src) {
    myImageModalProps.modalImageSrc = src;
    myImageModalProps.showModal = true;
}

function closeImageModal() {
    myImageModalProps.showModal = false;
}

function showSendMessageModal() {
    if (!usePage().props.user?.email) {
        alert("Please log in to message the event organizer!");
        return;
    }

    eventBus.$emit("showSendMessageModal");
}
</script>

<template>
    <EventPublicWrapper>
        <Head>
            <title>Event: {{ $page.props.event.name }}</title>

            <meta
                property="og:title"
                head-key="og:title"
                :content="`Event: ${$page.props.meta_event_name}`"
            />

            <meta
                name="description"
                head-key="description"
                :content="$page.props.meta_desc"
            />

            <meta
                property="og:description"
                :content="$page.props.meta_desc"
            />
        </Head>
        <div class="flex w-full md:max-w-xl lg:basis-2/3 xl:basis-1/2 2xl:basis-2/5">
            <div class="w-full space-y-4">
                <div
                    v-if="event.visibility === 'private'"
                    class="mb-4 rounded-lg border border-red-300 bg-red-50 p-4 text-red-800"
                    role="alert"
                >
                    <div class="flex items-center">
                        <svg
                            class="me-2 size-4 shrink-0"
                            aria-hidden="true"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                        >
                            <path
                                d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5ZM9.5 4a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3ZM12 15H8a1 1 0 0 1 0-2h1v-3H8a1 1 0 0 1 0-2h2a1 1 0 0 1 1 1v4h1a1 1 0 0 1 0 2Z"
                            />
                        </svg>
                        <span class="sr-only">Info</span>
                        <h3 class="text-lg font-medium">
                            This event is currently private. Only the event owner and team administrators can
                            access this page.
                        </h3>
                    </div>
                    <div class="mt-2 text-sm">
                        Once you are satisfied with all details, you can choose to make the event public or
                        unlisted.
                    </div>
                </div>

                <HeadingComponent class="rounded-lg bg-white p-4 shadow-lg sm:px-6">
                    <template #name
                        >Event information
                        {{
                            $page.props.user && $page.props.user.can_impersonate
                                ? ` (event_id: ${$page.props.event.id} user_id: ${$page.props.event.user_id})`
                                : ""
                        }}
                    </template>
                    <template #description
                        >{{ event.name }}
                        <span
                            class="inline-flex rounded-full px-2 text-sm font-semibold"
                            :class="{
                                'bg-green-100 text-green-800': event.visibility === 'public',
                                'bg-red-100 text-red-800': event.visibility === 'private',
                                'bg-slate-100 text-slate-800': event.visibility === 'unlisted',
                            }"
                        >
                            {{ event.visibility }}</span
                        ></template
                    >
                </HeadingComponent>
                <HeadingComponent
                    v-if="$page.props.can.view_sensitive_event"
                    class="rounded-lg bg-orange-50 p-4 shadow-lg sm:px-6"
                >
                    <template #name>Event administrator tools</template>
                    <template #description>
                        <div class="space-x-4 pt-4">
                            <Button
                                :href="
                                    route('event.edit.general', {
                                        hashid: $page.props.event.hashid,
                                        seo_slug: $page.props.event.seo_slug,
                                    })
                                "
                                class="h-6"
                                variant="outline"
                            >
                                <PencilIcon
                                    class="-ml-0.5 mr-1.5 size-4"
                                    aria-hidden="true"
                                />
                                Edit general info
                            </Button>
                        </div>
                    </template>
                </HeadingComponent>

                <div class="flex flex-col">
                    <div class="overflow-hidden rounded-lg bg-white shadow-lg">
                        <div class="border-t border-gray-100">
                            <dl class="divide-y divide-gray-100">
                                <div
                                    v-if="event.country_full"
                                    class="p-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6"
                                >
                                    <dt class="text-sm font-medium text-gray-900">Country</dt>
                                    <dd
                                        class="mt-1 text-sm tabular-nums leading-6 text-gray-700 sm:col-span-2 sm:mt-0"
                                    >
                                        <span
                                            :class="`border text-xl fi fi-${event.country.toLowerCase()}`"
                                        ></span>
                                        &nbsp;
                                        {{ event.country_full }}
                                    </dd>
                                </div>
                                <div
                                    v-if="event.hq_location"
                                    class="p-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6"
                                >
                                    <dt class="text-sm font-medium text-gray-900">Event headquarters</dt>
                                    <dd
                                        class="mt-1 text-sm tabular-nums leading-6 text-gray-700 sm:col-span-2 sm:mt-0"
                                    >
                                        <a
                                            :href="getDeviceNavigationAppUrl(event.hq_location)"
                                            target="_blank"
                                        >
                                            {{
                                                event.hq_location.coordinates
                                                    .join(",")
                                                    .split(",")
                                                    .reverse()
                                                    .join(", ")
                                            }}
                                            <ArrowTopRightOnSquareIcon
                                                class="inline size-5"
                                            ></ArrowTopRightOnSquareIcon>
                                        </a>
                                    </dd>
                                </div>
                                <div class="items-center p-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                                    <dt class="text-sm font-medium text-gray-900">Starts</dt>
                                    <dd
                                        v-if="event.start_at"
                                        class="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0"
                                    >
                                        {{
                                            DateTime.fromISO(event.start_at)
                                                .setZone(event.timezone)
                                                .toRelative()
                                        }}
                                        <br />
                                        <span
                                            class="cursor-pointer font-medium tabular-nums text-blue-600 hover:underline"
                                            @click="calendarToggle = !calendarToggle"
                                        >
                                            <FwbTooltip placement="right">
                                                <template #trigger>
                                                    {{
                                                        DateTime.fromISO(event.start_at)
                                                            .setZone(event.timezone)
                                                            .toLocaleString(DateTime.DATETIME_SHORT)
                                                    }}
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        fill="none"
                                                        viewBox="0 0 24 24"
                                                        stroke-width="1.5"
                                                        stroke="currentColor"
                                                        class="inline size-6"
                                                    >
                                                        <path
                                                            stroke-linecap="round"
                                                            stroke-linejoin="round"
                                                            d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5"
                                                        />
                                                    </svg>

                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        fill="none"
                                                        viewBox="0 0 24 24"
                                                        stroke-width="1.5"
                                                        stroke="currentColor"
                                                        class="-ml-4 mt-2 inline-block size-4"
                                                    >
                                                        <path
                                                            stroke-linecap="round"
                                                            stroke-linejoin="round"
                                                            d="M12 6v12m6-6H6"
                                                        />
                                                    </svg>
                                                </template>
                                                <template #content
                                                    >Add to calendar <br />
                                                    <br />
                                                    In user time:
                                                    {{ new Date(event.start_at).toLocaleString() }}</template
                                                >
                                            </FwbTooltip>
                                        </span>
                                        <div
                                            v-if="calendarToggle"
                                            class="p-4 pl-0 pt-0"
                                        >
                                            Add to calendar:
                                            <ul class="list-disc pl-4">
                                                <li v-for="cal in $page.props.calendar_links">
                                                    <a
                                                        class="block"
                                                        :href="cal.url"
                                                        target="_blank"
                                                    >
                                                        {{ cal.name }}
                                                        <svg
                                                            class="inline size-5"
                                                            fill="none"
                                                            stroke="currentColor"
                                                            viewBox="0 0 24 24"
                                                            xmlns="http://www.w3.org/2000/svg"
                                                        >
                                                            <path
                                                                stroke-linecap="round"
                                                                stroke-linejoin="round"
                                                                stroke-width="2"
                                                                d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                                                            ></path>
                                                        </svg>
                                                    </a>
                                                </li>
                                            </ul>
                                        </div>
                                    </dd>
                                </div>
                                <div class="items-center p-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                                    <dt class="text-sm font-medium text-gray-900">Ends</dt>
                                    <dd
                                        v-if="event.end_at"
                                        class="mt-1 text-sm tabular-nums leading-6 text-gray-700 sm:col-span-2 sm:mt-0"
                                    >
                                        <FwbTooltip placement="right">
                                            <template #trigger>
                                                {{
                                                    DateTime.fromISO(event.end_at)
                                                        .setZone(event.timezone)
                                                        .toRelative()
                                                }}
                                                <br />
                                                <span class="text-gray-400">
                                                    {{
                                                        DateTime.fromISO(event.end_at)
                                                            .setZone(event.timezone)
                                                            .toLocaleString(DateTime.DATETIME_SHORT)
                                                    }}
                                                </span>
                                            </template>
                                            <template #content
                                                >In user time:
                                                {{ new Date(event.end_at).toLocaleString() }}
                                            </template>
                                        </FwbTooltip>
                                    </dd>
                                </div>
                                <div class="items-center p-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                                    <dt class="text-sm font-medium text-gray-900">Registration opens</dt>
                                    <dd
                                        v-if="event.reg_open_from"
                                        class="mt-1 text-sm tabular-nums leading-6 text-gray-700 sm:col-span-2 sm:mt-0"
                                    >
                                        <FwbTooltip placement="right">
                                            <template #trigger>
                                                {{
                                                    DateTime.fromISO(event.reg_open_from)
                                                        .setZone(event.timezone)
                                                        .toRelative()
                                                }}
                                                <br />
                                                <span class="text-gray-400"
                                                    >{{
                                                        DateTime.fromISO(event.reg_open_from)
                                                            .setZone(event.timezone)
                                                            .toLocaleString(DateTime.DATETIME_SHORT)
                                                    }}
                                                </span>
                                            </template>
                                            <template #content
                                                >In user time:
                                                {{ new Date(event.reg_open_from).toLocaleString() }}
                                            </template>
                                        </FwbTooltip>
                                    </dd>
                                </div>
                                <div class="items-center p-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                                    <dt class="text-sm font-medium text-gray-900">Registration closes</dt>
                                    <dd
                                        v-if="event.reg_open_to"
                                        class="mt-1 text-sm tabular-nums leading-6 text-gray-700 sm:col-span-2 sm:mt-0"
                                    >
                                        <FwbTooltip placement="right">
                                            <template #trigger>
                                                {{
                                                    DateTime.fromISO(event.reg_open_to)
                                                        .setZone(event.timezone)
                                                        .toRelative()
                                                }}
                                                <br />
                                                <span class="text-gray-400">
                                                    {{
                                                        DateTime.fromISO(event.reg_open_to)
                                                            .setZone(event.timezone)
                                                            .toLocaleString(DateTime.DATETIME_SHORT)
                                                    }}
                                                </span>
                                            </template>
                                            <template #content
                                                >In user time:
                                                {{ new Date(event.reg_open_to).toLocaleString() }}
                                            </template>
                                        </FwbTooltip>
                                    </dd>
                                </div>
                                <div
                                    v-if="event.max_competitors"
                                    class="p-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6"
                                >
                                    <dt class="text-sm font-medium text-gray-900">Max entries</dt>
                                    <dd class="mt-1 text-sm tabular-nums leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                        {{ event.max_competitors }}
                                        <FwbTooltip
                                            v-if="is_event_full"
                                            placement="right"
                                            narrow
                                            class="inline-flex"
                                        >
                                            <template #trigger>
                                                <span class="cursor-help font-medium text-red-600 underline decoration-dotted"
                                                    >· full</span
                                                >
                                            </template>
                                            <template #content>
                                                All {{ event.max_competitors }} places are taken by pending or accepted
                                                entries. A place opens again if an entry is withdrawn or rejected.
                                                Contact the organizer to ask about a free place.
                                            </template>
                                        </FwbTooltip>
                                        <span
                                            v-else-if="!event.is_event_competitors_hidden"
                                            class="text-gray-400"
                                            >· {{ event.max_competitors - event.competitors_count }}
                                            {{ event.max_competitors - event.competitors_count === 1 ? 'place' : 'places' }}
                                            left</span
                                        >
                                    </dd>
                                </div>
                                <div class="p-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                                    <dt class="text-sm font-medium text-gray-900">Local time</dt>
                                    <dd class="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                        <FwbTooltip placement="right">
                                            <template #trigger>
                                                <LocalEventTime
                                                    :show-u-t-c="true"
                                                    luxon-preset="DATETIME_SHORT_WITH_SECONDS"
                                                />
                                                &nbsp;
                                                <a
                                                    target="_blank"
                                                    :href="
                                                        route('event.public.start-list-clock', {
                                                            hashid: event.hashid,
                                                            seo_slug: event.seo_slug,
                                                        })
                                                    "
                                                >
                                                    <ArrowTopRightOnSquareIcon
                                                        class="inline size-5"
                                                    ></ArrowTopRightOnSquareIcon>
                                                </a>
                                            </template>
                                            <template #content>Event timezone: {{ event.timezone }}</template>
                                        </FwbTooltip>
                                    </dd>
                                </div>
                                <div class="p-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                                    <dt class="text-sm font-medium text-gray-900">Organizer</dt>
                                    <dd
                                        class="mt-1 text-sm tabular-nums leading-6 text-gray-700 sm:col-span-2 sm:mt-0"
                                    >
                                        {{
                                            event.user && event.user.event_organizer_brand
                                                ? event.user.event_organizer_brand
                                                : "-"
                                        }}
                                        &nbsp;
                                        <Button
                                            class="h-6"
                                            @click="showSendMessageModal()"
                                            >Contact organizer
                                        </Button>
                                    </dd>
                                </div>
                                <!--                            <div
class="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6"
>
<dt
class="text-sm font-medium leading-6 text-gray-900"
>
Attachments
</dt>
<dd
class="mt-2 text-sm text-gray-900 sm:col-span-2 sm:mt-0"
>
<ul
role="list"
class="divide-y divide-gray-100 rounded-md border border-gray-200"
>
<li
class="flex items-center justify-between py-4 pl-4 pr-5 text-sm leading-6"
>
<div
class="flex w-0 flex-1 items-center"
>
<div
class="ml-4 flex min-w-0 flex-1 gap-2"
>
<span
class="truncate font-medium"
>resume_back_end_developer.pdf</span
>
<span
class="shrink-0 text-gray-400"
>2.4mb</span
>
</div>
</div>
<div class="ml-4 shrink-0">
<a
href="#"
>Download</a
>
</div>
</li>
<li
class="flex items-center justify-between py-4 pl-4 pr-5 text-sm leading-6"
>
<div
class="flex w-0 flex-1 items-center"
>
<div
class="ml-4 flex min-w-0 flex-1 gap-2"
>
<span
class="truncate font-medium"
>coverletter_back_end_developer.pdf</span
>
<span
class="shrink-0 text-gray-400"
>4.5mb</span
>
</div>
</div>
<div class="ml-4 shrink-0">
<a
href="#"
>Download</a
>
</div>
</li>
</ul>
</dd>
</div>-->
                            </dl>
                        </div>
                    </div>
                </div>

                <ul class="flex flex-col space-y-4">
                    <Disclosure
                        v-for="(info, idx) in infos"
                        :key="idx"
                        v-slot="{ open, close }"
                        as="li"
                        class="overflow-hidden rounded-lg bg-white shadow-lg"
                    >
                        <DisclosureButton
                            :id="'faq-' + idx"
                            :ref="(el) => (disclosure[idx] = close)"
                            as="h2"
                            class="flex cursor-pointer flex-row items-center justify-between p-3 sm:p-4"
                            @click="closeOthers(idx)"
                        >
                            <span class="px-1 text-base font-semibold leading-7 text-gray-900">{{
                                info.title
                            }}</span>
                            <span class="ml-6 flex h-7 items-center">
                                <svg
                                    :class="open ? 'rotate-180 duration-500' : 'rotate-0 duration-500'"
                                    class="size-6 fill-current text-blue-700"
                                    viewBox="0 0 20 20"
                                >
                                    <path
                                        d="M13.962,8.885l-3.736,3.739c-0.086,0.086-0.201,0.13-0.314,0.13S9.686,12.71,9.6,12.624l-3.562-3.56C5.863,8.892,5.863,8.611,6.036,8.438c0.175-0.173,0.454-0.173,0.626,0l3.25,3.247l3.426-3.424c0.173-0.172,0.451-0.172,0.624,0C14.137,8.434,14.137,8.712,13.962,8.885 M18.406,10c0,4.644-3.763,8.406-8.406,8.406S1.594,14.644,1.594,10S5.356,1.594,10,1.594S18.406,5.356,18.406,10 M17.521,10c0-4.148-3.373-7.521-7.521-7.521c-4.148,0-7.521,3.374-7.521,7.521c0,4.147,3.374,7.521,7.521,7.521C14.148,17.521,17.521,14.147,17.521,10"
                                    ></path>
                                </svg>
                            </span>
                        </DisclosureButton>
                        <transition
                            :show="open"
                            enter-active-class="duration-100 transition-all"
                            enter-from-class="max-h-0"
                            enter-to-class="max-h-64"
                            leave-active-class="duration-100 transition-all"
                            leave-from-class="max-h-64"
                            leave-to-class="max-h-0"
                        >
                            <DisclosurePanel
                                as="div"
                                class="max-h-0 overflow-hidden border-l-2 border-blue-600"
                            >
                                <article
                                    class="prose max-w-none overflow-hidden whitespace-pre-wrap break-words bg-gray-50 p-3 px-4 py-5 text-gray-900 [overflow-wrap:anywhere] sm:p-6"
                                    v-html="info.terms ? info.terms : linkify(entityDecoder(info.description))"
                                ></article>
                            </DisclosurePanel>
                        </transition>
                    </Disclosure>

                    <Disclosure
                        v-if="!$page.props.event.is_event_penalties_hidden"
                        v-slot="{ open, close }"
                        key="500"
                        as="li"
                        class="overflow-hidden rounded-lg bg-white shadow-lg"
                    >
                        <DisclosureButton
                            id="penalty_rules"
                            :ref="(el) => (disclosure[500] = close)"
                            as="h2"
                            class="flex cursor-pointer flex-row items-center justify-between p-3 sm:p-4"
                            @click="closeOthers(500)"
                        >
                            <span class="px-1 text-base font-semibold leading-7 text-gray-900"
                                >Penalties Policy</span
                            >
                            <span class="ml-6 flex h-7 items-center">
                                <svg
                                    :class="open ? 'rotate-180 duration-500' : 'rotate-0 duration-500'"
                                    class="size-6 fill-current text-blue-700"
                                    viewBox="0 0 20 20"
                                >
                                    <path
                                        d="M13.962,8.885l-3.736,3.739c-0.086,0.086-0.201,0.13-0.314,0.13S9.686,12.71,9.6,12.624l-3.562-3.56C5.863,8.892,5.863,8.611,6.036,8.438c0.175-0.173,0.454-0.173,0.626,0l3.25,3.247l3.426-3.424c0.173-0.172,0.451-0.172,0.624,0C14.137,8.434,14.137,8.712,13.962,8.885 M18.406,10c0,4.644-3.763,8.406-8.406,8.406S1.594,14.644,1.594,10S5.356,1.594,10,1.594S18.406,5.356,18.406,10 M17.521,10c0-4.148-3.373-7.521-7.521-7.521c-4.148,0-7.521,3.374-7.521,7.521c0,4.147,3.374,7.521,7.521,7.521C14.148,17.521,17.521,14.147,17.521,10"
                                    ></path>
                                </svg>
                            </span>
                        </DisclosureButton>
                        <transition
                            :show="open"
                            enter-active-class="duration-100 transition-all"
                            enter-from-class="max-h-0"
                            enter-to-class="max-h-64"
                            leave-active-class="duration-100 transition-all"
                            leave-from-class="max-h-64"
                            leave-to-class="max-h-0"
                        >
                            <DisclosurePanel
                                as="div"
                                class="max-h-0 overflow-hidden border-l-2 border-blue-600"
                            >
                                <article
                                    class="prose max-w-none space-y-6 overflow-hidden whitespace-pre-wrap break-words bg-gray-50 p-3 px-4 py-5 text-gray-900 [overflow-wrap:anywhere] sm:p-6"
                                >
                                    <div>
                                        <strong>Speeding tolerance limits:</strong> A penalty applies if a
                                        competitor exceeds the speed limit by more than
                                        {{ myPenalties.speeding_grace_period_kmh }} km/h for longer than
                                        {{ myPenalties.speeding_grace_period_s }}
                                        seconds.
                                        <br />
                                    </div>
                                    <div>
                                        <strong>Speeding penalty based on:</strong>
                                        {{
                                            myPenalties.penalty_based_on === "scaling"
                                                ? "gained time"
                                                : myPenalties.penalty_based_on
                                        }}
                                        <TooltipIcon v-if="myPenalties.penalty_based_on === 'scaling'">
                                            <div class="max-w-2xl">
                                                The speeding penalty is calculated based on the time gained.
                                                <br />
                                                <br />
                                                If a competitor exceeds the speed limit, the "exact seconds
                                                gained" are determined by taking into account both the extent to
                                                which the speed limit was exceeded and the duration of the
                                                violation. A penalty factor ({{
                                                    myPenalties.scaling_penalty_factor
                                                }}x) is then applied to the gained time.
                                                <br />
                                                <br />
                                                For example, if a competitor gains 2 seconds by speeding and the
                                                penalty factor is "50", then the penalty will be calculated as
                                                follows: 2 gained seconds x 50 penalty factor = 100 seconds
                                                penalty.
                                            </div>
                                        </TooltipIcon>
                                        <TooltipIcon v-if="myPenalties.penalty_based_on !== 'scaling'">
                                            <div class="max-w-2xl">
                                                <table class="table table-auto bg-white text-black">
                                                    <thead>
                                                        <tr>
                                                            <th
                                                                class="border border-gray-400 p-2 text-left text-sm"
                                                            >
                                                                Speeding
                                                            </th>
                                                            <th
                                                                class="border border-gray-400 p-2 text-left text-sm"
                                                            >
                                                                Penalty seconds for each
                                                                {{
                                                                    myPenalties.penalty_based_on === "time"
                                                                        ? "second"
                                                                        : "km/h"
                                                                }}
                                                            </th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        <tr>
                                                            <td
                                                                class="border border-gray-400 p-2 text-left text-sm"
                                                            >
                                                                &lt; 10
                                                                {{
                                                                    myPenalties.penalty_based_on === "time"
                                                                        ? "seconds"
                                                                        : "km/h"
                                                                }}
                                                            </td>
                                                            <td
                                                                class="border border-gray-400 p-2 text-left text-sm"
                                                            >
                                                                {{ myPenalties.speeding_up_to_10 }}
                                                            </td>
                                                        </tr>
                                                        <tr>
                                                            <td
                                                                class="border border-gray-400 p-2 text-left text-sm"
                                                            >
                                                                &lt; 20
                                                                {{
                                                                    myPenalties.penalty_based_on === "time"
                                                                        ? "seconds"
                                                                        : "km/h"
                                                                }}
                                                            </td>
                                                            <td
                                                                class="border border-gray-400 p-2 text-left text-sm"
                                                            >
                                                                {{ myPenalties.speeding_up_to_20 }}
                                                            </td>
                                                        </tr>
                                                        <tr>
                                                            <td
                                                                class="border border-gray-400 p-2 text-left text-sm"
                                                            >
                                                                &lt; 30
                                                                {{
                                                                    myPenalties.penalty_based_on === "time"
                                                                        ? "seconds"
                                                                        : "km/h"
                                                                }}
                                                            </td>
                                                            <td
                                                                class="border border-gray-400 p-2 text-left text-sm"
                                                            >
                                                                {{ myPenalties.speeding_up_to_30 }}
                                                            </td>
                                                        </tr>
                                                        <tr>
                                                            <td
                                                                class="border border-gray-400 p-2 text-left text-sm"
                                                            >
                                                                &gt;= 30
                                                                {{
                                                                    myPenalties.penalty_based_on === "time"
                                                                        ? "seconds"
                                                                        : "km/h"
                                                                }}
                                                            </td>
                                                            <td
                                                                class="border border-gray-400 p-2 text-left text-sm"
                                                            >
                                                                {{ myPenalties.speeding_more_than_30 }}
                                                            </td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            </div>
                                        </TooltipIcon>
                                    </div>
                                    <div>
                                        <strong>No stop at STOP sign:</strong> Applies a
                                        {{ myPenalties.no_stop_penalty_s }} second penalty if the minimum speed
                                        within the stop point radius exceeds
                                        {{ myPenalties.no_stop_threshold }} km/h.
                                    </div>
                                    <div>
                                        <strong>Early exit from the neutralization zone (DN):</strong> will be
                                        penalized as follows:
                                        {{ myPenalties.neutralization_early_start_penalty_s }} seconds penalty
                                        for every
                                        {{ myPenalties.neutralization_early_start_penalty_for_each }} seconds.
                                    </div>
                                    <div>
                                        <strong>Penalties for missed waypoints:</strong> <br />
                                        <div
                                            v-for="wpt in myPenalties.waypoints"
                                            :key="wpt.id"
                                        >
                                            {{ wpt.name }}:
                                            <span v-if="wpt.missed_penalty !== 'SP'"
                                                >{{ wpt.missed_penalty }}-minute penalty
                                                <span v-if="wpt.hint">({{ wpt.hint }})</span></span
                                            >

                                            <span v-if="wpt.missed_penalty === 'SP'">Section Penalty</span>
                                        </div>
                                    </div>
                                    <div>
                                        <strong>Section Penalty:</strong> The Section Penalty is calculated by
                                        multiplying the corresponding section's maximum allowed time limit (see
                                        event itinerary) by the factor:
                                        {{ myPenalties.section_penalty_factor }}.
                                    </div>
                                </article>
                            </DisclosurePanel>
                        </transition>
                    </Disclosure>
                </ul>

                <div
                    v-if="$page.props.entity_images.length > 0"
                    class="h-auto max-w-full rounded-lg border bg-white p-3 shadow-lg sm:p-4"
                >
                    <div class="space-y-4">
                        <img
                            v-for="logo in $page.props.entity_images"
                            :key="logo"
                            :src="logo"
                            class="cursor-pointer rounded-md"
                            alt="Event poster"
                            @click="showImageModal(logo)"
                        />
                    </div>
                </div>
            </div>
        </div>

        <Modal
            :show="myImageModalProps.showModal"
            max-width="min"
            @close="closeImageModal"
        >
            <div @click="closeImageModal()">
                <img
                    :src="myImageModalProps.modalImageSrc"
                    class="max-h-full rounded border bg-white p-1 shadow-lg sm:max-h-screen sm:max-w-screen-sm md:max-w-screen-md lg:max-w-screen-lg"
                />
            </div>
        </Modal>

        <template #map>
            <EventInformationMap
                class="content-full-screen-height-mt-4 hidden flex-1 rounded-lg bg-white shadow-lg lg:flex"
            />
        </template>

        <SendMessageModal
            v-if="usePage().props.user"
            entity-name="event"
        ></SendMessageModal>
    </EventPublicWrapper>
</template>
